import { niceScale } from './chartScale';

interface FollowerReachabilityChartProps {
  data: { label: string; val: number }[];
}

// Figma "Audience Fit — Chart 917" (node 2019:69389): a vertical bar per
// following-range bucket, dashed gridlines, and the % above each bar —
// same "nice scale" treatment as the Audience Fit chart, single series.
const SCALE_H = 140;
const BAR_COLOR = '#0D9488';

export function FollowerReachabilityChart({ data }: FollowerReachabilityChartProps) {
  const maxVal = Math.max(...data.map((d) => d.val), 0);
  const { axisMax, ticks } = niceScale(maxVal);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2.5" style={{ height: SCALE_H }}>
        {/* Y-axis */}
        <div className="relative w-8 shrink-0">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute right-0 -translate-y-1/2 text-[11px]/4 font-medium text-muted-foreground"
              style={{ top: `${(1 - t / axisMax) * 100}%` }}
            >
              {t}
            </span>
          ))}
        </div>

        {/* Chart — bars sit flush on the 0 gridline; the x-axis label row
            below is a sibling, not a flex child in here, so it can't push
            the bars' baseline off zero. */}
        <div className="relative flex-1">
          {ticks.map((t) => (
            <div
              key={t}
              className="absolute inset-x-0 border-t border-dashed border-[#e5e5e5]"
              style={{ top: `${(1 - t / axisMax) * 100}%` }}
            />
          ))}

          <div className="absolute inset-0 flex items-end justify-between gap-2">
            {data.map((d) => {
              const barH = (d.val / axisMax) * SCALE_H;
              return (
                <div key={d.label} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                  <span className="text-[12px] font-bold leading-none text-foreground">{d.val}%</span>
                  <div
                    className="w-full max-w-[48px] rounded-t-[4px]"
                    style={{ height: `${barH}px`, background: BAR_COLOR }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* X-axis: following-range bucket per bar, laid out as a sibling row
          so it can't affect the bars' 0-baseline above. */}
      <div className="flex gap-2.5">
        <div className="w-8 shrink-0" />
        <div className="flex flex-1 justify-between gap-2">
          {data.map((d) => (
            <span
              key={d.label}
              className="flex-1 truncate text-center text-[11px] font-medium text-muted-foreground"
            >
              {d.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
